import {isFeatureEnabled} from '@github-ui/feature-flags'
import {getBaseFetchHeaders} from '@github-ui/fetch-headers'
import {IncludeFragment} from '@github-ui/include-fragment-react'
import {type ReactPartialAnchorProps, useExternalAnchor} from '@github-ui/react-core/react-partial-anchor'
import {testIdProps} from '@github-ui/test-id-props'
import {getUtf8StringLength} from '@github-ui/text'
import {useClickAnalytics} from '@github-ui/use-analytics'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {AlertIcon} from '@primer/octicons-react'
import {Banner, Checkbox, type DialogProps, FormControl, Link, type SelectPanelItemInput, Spinner} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {
  type FormEvent,
  forwardRef,
  type RefObject,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'

import {EditStatusAutoComplete, MESSAGE_MAX_LENGTH} from './components/EditStatusAutoComplete'
import {Emoji, type EmojiAttributes} from './components/Emoji'
import EmojiPicker from './components/EmojiPicker'
import ExpirationSelect from './components/ExpirationSelect'
import OrganizationSelect, {type Organization, type OrganizationOption} from './components/OrganizationSelect'
import SuggestionChips, {type Suggestion} from './components/SuggestionChips'
import styles from './UserStatusDialog.module.css'

const defaultCategory = 'USER_STATUS_DIALOG'

export interface UserStatusFieldOptions {
  expirationIntervals?: Array<{
    label: string
    time: string
  }>
  organizationOptions?: OrganizationOption[]
  suggestions?: Suggestion[]
}

export interface UserStatusFormData {
  message?: string
  messageHtml?: string
  emojiAttributes?: EmojiAttributes
  limitedAvailability?: boolean
  organization?: Organization
  expiresIn?: string | null
  expiresAt?: string | null
}

export interface UserStatus extends UserStatusFormData, UserStatusFieldOptions {}

export interface UserPreferences {
  tone?: number
}

async function saveUserStatus(body: FormData) {
  // put method is required for the endpoint to accept the request
  body.set('_method', 'put')
  const response = await verifiedFetch('/users/status', {
    method: 'POST',
    headers: {...getBaseFetchHeaders(), Accept: 'application/json'},
    body,
  })

  return response.json() as Promise<UserStatus>
}

export function useFocus() {
  const [focusTarget, setFocusTarget] = useState<RefObject<HTMLDivElement | null> | null>(null)

  useEffect(() => {
    if (focusTarget === null) {
      return
    }

    if (focusTarget.current) {
      focusTarget.current.focus()
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setFocusTarget(null)
  }, [focusTarget])

  return setFocusTarget
}

const getEmojiValue = (emojiAttributes: EmojiAttributes | undefined, tone: number | undefined) => {
  if (!emojiAttributes) {
    return undefined
  }

  return {
    text: emojiAttributes.name,
    id: emojiAttributes.name,
    leadingVisual: () => <Emoji {...emojiAttributes} tone={tone} />,
  }
}

export type UserStatusDialogProps = {
  onClose: (statusResponse?: Promise<UserStatus> | string) => void
  userStatusData?: UserStatus
  userPreferences?: UserPreferences
} & Pick<DialogProps, 'returnFocusRef'>

export const UserStatusDialog = forwardRef<HTMLDivElement, UserStatusDialogProps>(
  ({onClose, userPreferences, userStatusData, returnFocusRef}, ref) => {
    const {sendClickAnalyticsEvent} = useClickAnalytics()
    const [fragmentLoaded, setFragmentLoaded] = useState(false)
    const bannerRef = useRef<HTMLDivElement>(null)
    const [showBanner, setShowBanner] = useState(false)
    const [characterCount, setCharacterCount] = useState<number>(() =>
      getUtf8StringLength(userStatusData?.message ?? ''),
    )
    const [selectedOrganization, setSelectedOrganization] = useState<Organization | undefined>(
      userStatusData?.organization,
    )
    const [inputValue, setInputValue] = useState<string>(userStatusData?.message || '')
    const inputRef = useRef<HTMLInputElement>(null)
    const initialSelected = useMemo(
      () => getEmojiValue(userStatusData?.emojiAttributes, userPreferences?.tone),
      [userStatusData, userPreferences],
    )

    const [emojiValue, setEmojiValue] = useState<SelectPanelItemInput | undefined>(initialSelected)
    const globalNavReactEditStatusDialog = isFeatureEnabled('global_nav_react_edit_status_dialog')
    const reactProfileUserStatusDialogEnabled = isFeatureEnabled('react_profile_user_status_dialog_enabled')
    const fragmentRef = useRef<HTMLDivElement>(null)
    const formId = useId()
    const focus = useFocus()

    // When the user submits the form, we need to save the status and dispatch a custom event to update the profile view.
    // The profile view is currently a react partial and only updates when it receives the 'react-user-status-dialog:submit' event.
    // This event should be removed once the profile view is fully React.
    const formSubmissionCustomEventInternal = useCallback(
      async (statusResponse?: Promise<UserStatus> | string): Promise<void> => {
        if (reactProfileUserStatusDialogEnabled && statusResponse instanceof Promise) {
          try {
            const response = await statusResponse
            // send an event from .js-user-status-container
            const eventDetail = {
              emojiAttributes:
                typeof response.emojiAttributes === 'object' && response.emojiAttributes !== null
                  ? response.emojiAttributes
                  : '',
              message: response.message ?? '',
            }
            const event = new CustomEvent('react-user-status-dialog:submit', {
              bubbles: true,
              detail: eventDetail,
            })
            document.querySelector('.js-user-status-container')?.dispatchEvent(event)
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to save user status:', error)
          }
        }
        onClose(statusResponse)
      },
      [reactProfileUserStatusDialogEnabled, onClose],
    )

    const onFormSubmit = useCallback(
      (e: FormEvent) => {
        e.preventDefault()
        const data = new FormData(e.target as HTMLFormElement)
        const anyValues = [...data.entries()].filter(([_, value]) => {
          if (typeof value === 'string') {
            return value.trim() !== ''
          }
          return value
        })

        if (anyValues.length && !data.get('emoji') && !data.get('message')) {
          setShowBanner(true)
          focus(bannerRef)
          return
        }

        sendClickAnalyticsEvent({category: defaultCategory, action: 'SET_USER_STATUS'})
        // call form submission custom event after saving user status
        void formSubmissionCustomEventInternal(saveUserStatus(data))
      },
      [formSubmissionCustomEventInternal, sendClickAnalyticsEvent, focus, bannerRef],
    )

    const onClearStatus = useCallback(() => {
      sendClickAnalyticsEvent({category: defaultCategory, action: 'CLEAR_USER_STATUS'})
      void formSubmissionCustomEventInternal(Promise.resolve(saveUserStatus(new FormData())))
    }, [sendClickAnalyticsEvent, formSubmissionCustomEventInternal])

    useEffect(() => {
      const fragment = fragmentRef.current
      if (!fragment) {
        return
      }

      function loaded() {
        setFragmentLoaded(true)
      }

      fragment.addEventListener('load', loaded)
      return () => {
        fragment.removeEventListener('load', loaded)
      }
    }, [fragmentRef])

    const selectSuggestion = useCallback(
      ({emoji, message}: {emoji?: EmojiAttributes | undefined; message: string}) => {
        setEmojiValue(getEmojiValue(emoji, userPreferences?.tone))
        if (inputRef.current) {
          inputRef.current.value = message
        }
        setInputValue(message)
      },
      [setEmojiValue, userPreferences, inputRef],
    )

    return (
      <Dialog
        width="large"
        title="Edit status"
        onClose={onClose}
        returnFocusRef={returnFocusRef}
        footerButtons={[
          {
            buttonType: 'normal',
            content: 'Clear status',
            onClick: onClearStatus,
          },
          {
            buttonType: 'primary',
            type: 'submit',
            content: 'Set status',
            form: formId,
            disabled: !globalNavReactEditStatusDialog && !fragmentLoaded,
          },
        ]}
        ref={ref}
      >
        {showBanner && (
          <Banner variant="critical" className="mb-3" ref={bannerRef} data-testid="user-status-error-banner">
            <Banner.Title>
              Either an{' '}
              <Link href="#user-status-emoji-picker-button" inline>
                Emoji
              </Link>{' '}
              or a{' '}
              <Link href="#user-status-status-input" inline>
                Status
              </Link>{' '}
              is required.
            </Banner.Title>
          </Banner>
        )}
        <form id={formId} onSubmit={onFormSubmit} className="user-status-dialog-fragment js-user-status-container">
          {globalNavReactEditStatusDialog ? (
            userStatusData !== undefined ? (
              <>
                <h2 className={styles.whatsHappeningHeading}>What&apos;s happening</h2>
                <div className={styles.statusContainer}>
                  <EmojiPicker tone={userPreferences?.tone || 0} value={emojiValue} setValue={setEmojiValue} />
                  <FormControl className={styles.statusFormControl} id="user-status-status-input">
                    <FormControl.Label visuallyHidden>Status</FormControl.Label>
                    <EditStatusAutoComplete
                      ref={inputRef}
                      setInputValue={setInputValue}
                      defaultValue={userStatusData?.message || ''}
                      organization={selectedOrganization}
                      onCharacterCountChange={setCharacterCount}
                      tone={userPreferences?.tone || 0}
                    />
                    <FormControl.Caption className={styles.statusCaption}>
                      {characterCount}/{MESSAGE_MAX_LENGTH} characters
                    </FormControl.Caption>
                  </FormControl>
                </div>
                {userStatusData?.suggestions && (
                  <SuggestionChips
                    updateInputs={selectSuggestion}
                    suggestions={userStatusData?.suggestions}
                    inputValue={inputValue}
                    emojiValue={emojiValue}
                    tone={userPreferences?.tone || 0}
                  />
                )}
                <FormControl className="border-bottom pb-3 mb-2">
                  <FormControl.Label>Busy</FormControl.Label>
                  <Checkbox
                    value={'1'}
                    name={'limited_availability'}
                    defaultChecked={userStatusData?.limitedAvailability || false}
                    data-testid="user-status-limited-availability-checkbox"
                    onChange={e => {
                      sendClickAnalyticsEvent({
                        category: defaultCategory,
                        action: 'SET_BUSY_USER_STATUS',
                        label: e.target.checked ? 'true' : 'false',
                      })
                    }}
                  />
                  <FormControl.Caption>
                    When others mention you, assign you, or request your review, GitHub will let them know that you have
                    limited availability.
                  </FormControl.Caption>
                </FormControl>
                <FormControl className="mb-2">
                  <FormControl.Label>Expiration</FormControl.Label>
                  <ExpirationSelect
                    expiresIn={userStatusData?.expiresIn}
                    expiresAt={userStatusData?.expiresAt}
                    expirationIntervals={userStatusData?.expirationIntervals}
                  />
                  <FormControl.Caption>Your status will be cleared after the selected time.</FormControl.Caption>
                </FormControl>
                {userStatusData?.organizationOptions !== undefined && (
                  <FormControl>
                    <FormControl.Label>Visible to</FormControl.Label>
                    <OrganizationSelect
                      defaultValue={userStatusData?.organization?.id}
                      organizationOptions={userStatusData?.organizationOptions}
                      onChange={e => {
                        const selectedOrg = userStatusData?.organizationOptions?.find(
                          org => org.value.toString() === e.target.value,
                        )

                        setSelectedOrganization(
                          selectedOrg ? {id: selectedOrg.value, globalRelayId: selectedOrg.globalRelayId} : undefined,
                        )
                      }}
                    />
                    <FormControl.Caption>Limit status visibility to a single organization.</FormControl.Caption>
                  </FormControl>
                )}
              </>
            ) : (
              <p className="text-center mt-3">
                <Spinner delay srText={'Loading user status'} />
              </p>
            )
          ) : (
            <IncludeFragment
              src={`/users/status`}
              accept="text/fragment+html"
              ref={fragmentRef}
              {...testIdProps('user-status-dialog-include-fragment')}
            >
              <p className="text-center mt-3" data-hide-on-error>
                <Spinner />
              </p>
              <p className="flash flash-error mb-0 mt-2" data-show-on-error hidden>
                <AlertIcon />
                Sorry, something went wrong and we were not able to fetch the user settings form
              </p>
            </IncludeFragment>
          )}
        </form>
      </Dialog>
    )
  },
)

UserStatusDialog.displayName = 'UserStatusDialog'

export function ExternallyAnchoredUserStatusDialog(
  props: Omit<UserStatusDialogProps, 'onClose' | 'anchorRef'> & Required<ReactPartialAnchorProps>,
) {
  const {ref: anchorRef, open, setOpen} = useExternalAnchor(props.reactPartialAnchor)

  const onClose = useCallback(() => {
    setOpen(false)
  }, [setOpen])

  return open ? <UserStatusDialog returnFocusRef={anchorRef} onClose={onClose} {...props} /> : null
}

export function UserStatusDialogPartial(
  props: Pick<UserStatusDialogProps, 'userStatusData' | 'userPreferences'> & ReactPartialAnchorProps,
) {
  if (props.reactPartialAnchor) {
    return <ExternallyAnchoredUserStatusDialog reactPartialAnchor={props.reactPartialAnchor} {...props} />
  }

  return null
}

try{ ExternallyAnchoredUserStatusDialog.displayName ||= 'ExternallyAnchoredUserStatusDialog' } catch {}
try{ UserStatusDialogPartial.displayName ||= 'UserStatusDialogPartial' } catch {}